import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, FileText, Loader } from 'lucide-react';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProofApprovalPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [order, setOrder] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofBlobUrl, setProofBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Define fetchOrderAndProof BEFORE useEffect
  const fetchOrderAndProof = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/enhanced-orders/${orderId}`,

      );
      setOrder(response.data);

      // Check for auto-generated proof first, then manual proof
      const proofId = response.data.auto_generated_proof_id || response.data.proof_file_id;

      if (proofId) {
        setProofFile({
          id: proofId,
          url: `${API_URL}/api/files/download/${proofId}`,
          isAutoGenerated: !!response.data.auto_generated_proof_id
        });

        // Fetch the PDF as a blob with authentication
        try {
          const pdfResponse = await axios.get(
            `${API_URL}/api/files/download/${proofId}`,
            {
              responseType: 'blob'
            }
          );

          // Create a blob URL that doesn't require authentication
          const blobUrl = URL.createObjectURL(pdfResponse.data);
          setProofBlobUrl(blobUrl);
        } catch (pdfError) {
          log.error('Error loading proof PDF:', pdfError);
          // Continue without proof preview
        }
      }
    } catch (error) {
      alert('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchOrderAndProof();
  }, [isAuthenticated, authLoading, orderId, navigate, fetchOrderAndProof]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (proofBlobUrl) {
        URL.revokeObjectURL(proofBlobUrl);
      }
    };
  }, [proofBlobUrl]);

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this proof? This action cannot be undone.')) {
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/enhanced-orders/${orderId}/approve-proof`,
        {},

      );

      alert('Proof approved successfully! Your order will now proceed to production.');
      navigate('/po-history');
    } catch (error) {
      alert('Failed to approve proof. Please try again.');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionNotes.trim()) {
      alert('Please provide rejection notes explaining what needs to be changed.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/enhanced-orders/${orderId}/reject-proof`,
        { rejection_notes: rejectionNotes },

      );

      alert('Proof rejected. Our team will review your notes and provide a revised proof.');
      navigate('/po-history');
    } catch (error) {
      alert('Failed to reject proof. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader className="w-12 h-12 animate-spin text-[#4A7C59]" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Order not found</div>
      </div>
    );
  }

  // Match ReviewOrder page currency resolution (order.currency → user.country → USD).
  const activeCurrency = (() => {
    const c = (order?.currency || '').toUpperCase();
    if (c === 'CAD' || c === 'USD') return c;
    return user?.country === 'Canada' ? 'CAD' : 'USD';
  })();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(activeCurrency === 'CAD' ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: activeCurrency,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <img
            src="https://customer-assets.emergentagent.com/job_layout-45/artifacts/nxqzytgg_EG3%28includes%20white%29.png"
            alt="Evergreen Graphics Group Inc"
            className="h-24 w-24 object-contain cursor-pointer"
            onClick={() => navigate('/splash')}
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Review Your Digital Proof</h1>
          <p className="text-gray-600 mb-8">
            Order Number: <span className="font-semibold text-[#4A7C59]">{order.order_number}</span>
          </p>

          {/* Order Details */}
          <div className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Order Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Total Items:</span>{' '}
                <span className="text-gray-600">{order.items?.length || 0}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total Cost:</span>{' '}
                <span className="text-gray-600">{formatCurrency(order.total_price || 0)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>{' '}
                <span className="text-gray-600 capitalize">{order.status?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>

          {/* Proof Display */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#4A7C59]" />
              Digital Proof
              {proofFile?.isAutoGenerated && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                  Auto-Generated
                </span>
              )}
            </h2>
            {proofFile ? (
              <div className="border-2 border-[#4A7C59] rounded-lg overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-green-50">
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-[#4A7C59]" />
                    <div>
                      <span className="font-medium text-gray-800">Artwork Proof PDF</span>
                      {proofFile.isAutoGenerated && (
                        <p className="text-xs text-gray-600 mt-1">
                          This proof was automatically generated with your uploaded artwork
                        </p>
                      )}
                    </div>
                  </div>
                  <a
                    href={proofBlobUrl || proofFile.url}
                    download={`proof_${order.order_number}.pdf`}
                    className="px-4 py-2 bg-[#4A7C59] text-white rounded-lg hover:bg-[#3d6849] transition-colors font-medium text-sm"
                  >
                    Download PDF
                  </a>
                </div>

                {/* PDF Viewer - Responsive with proper aspect ratio */}
                <div className="bg-white p-4 flex flex-col items-center">
                  {proofBlobUrl ? (
                    <div className="w-full flex flex-col items-center">
                      <iframe
                        src={`${proofBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        className="border-2 border-gray-300 rounded shadow-lg"
                        style={{
                          width: '100%',
                          maxWidth: '850px',
                          aspectRatio: '8.5 / 11',
                          display: 'block'
                        }}
                        title="Proof Preview"
                      />
                      <p className="text-xs text-gray-500 text-center mt-3">
                        💡 Tip: Download the PDF for the best viewing experience. You can zoom and review all details.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center border-2 border-gray-300 rounded bg-gray-50"
                         style={{ width: '100%', maxWidth: '850px', aspectRatio: '8.5 / 11' }}>
                      <div className="text-center">
                        <Loader className="w-12 h-12 text-[#4A7C59] animate-spin mx-auto mb-3" />
                        <p className="text-gray-600">Loading proof...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-gray-50">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">No proof available yet</p>
                <p className="text-sm text-gray-500">
                  Your proof is being prepared. You'll be notified when it's ready for review.
                </p>
              </div>
            )}
          </div>

          {/* Important Notice */}
          <div className="mb-8 p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <h3 className="font-bold text-yellow-800 mb-2">⚠️ Important - Please Review Carefully</h3>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
              <li>Check all text for spelling and grammar errors</li>
              <li>Verify colors match your expectations</li>
              <li>Confirm dimensions and layout are correct</li>
              <li>Ensure all artwork elements are properly positioned</li>
              <li>Once approved, changes cannot be made</li>
            </ul>
          </div>

          {/* Action Buttons */}
          {order.status === 'proof_pending' && (
            <div className="flex flex-col sm:flex-row gap-4">
              {!showRejectForm ? (
                <>
                  <Button
                    onClick={handleApprove}
                    disabled={submitting || !proofFile}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-6 text-lg flex items-center justify-center gap-3"
                  >
                    <CheckCircle className="w-6 h-6" />
                    {submitting ? 'Processing...' : 'APPROVE PROOF'}
                  </Button>
                  <Button
                    onClick={() => setShowRejectForm(true)}
                    disabled={submitting}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-6 text-lg flex items-center justify-center gap-3"
                  >
                    <XCircle className="w-6 h-6" />
                    REJECT PROOF
                  </Button>
                </>
              ) : (
                <div className="w-full space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please explain what needs to be changed: <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionNotes}
                      onChange={(e) => setRejectionNotes(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Describe the changes needed..."
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button
                      onClick={handleReject}
                      disabled={submitting || !rejectionNotes.trim()}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4"
                    >
                      {submitting ? 'Submitting...' : 'Submit Rejection'}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowRejectForm(false);
                        setRejectionNotes('');
                      }}
                      disabled={submitting}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-4"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {order.status !== 'proof_pending' && (
            <div className="text-center p-6 bg-gray-100 rounded-lg">
              <p className="text-gray-700 font-medium">
                {order.status === 'proof_approved' && '✅ Proof has been approved. Your order is in production.'}
                {order.status === 'proof_rejected' && '⚠️ Proof was rejected. A revised proof will be sent soon.'}
                {order.status === 'in_production' && '🔧 Your order is currently in production.'}
                {order.status === 'completed' && '✅ Your order is complete!'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProofApprovalPage;
